"use client";
import type { Locale } from "@i18n/react";
import { useLocale } from "@i18n/react";
import { fontFamilies, useThemeValues } from "@packages/ui";
import { polyfillCountryFlagEmojis } from "country-flag-emoji-polyfill";
import de from "emoji-picker-element/i18n/de";
import en from "emoji-picker-element/i18n/en";
import es from "emoji-picker-element/i18n/es";
import fr from "emoji-picker-element/i18n/fr";
import it from "emoji-picker-element/i18n/it";
import pl from "emoji-picker-element/i18n/pl";
import pt from "emoji-picker-element/i18n/pt_BR";
import type { EmojiClickEvent } from "emoji-picker-element/shared";
import type { RefObject } from "react";
import { createElement, useEffect, useMemo, useState } from "react";

export type EmojiPickerProps =
  | {
      /** Use for uncontrolled input elements, not components with state management */
      inputRef: RefObject<HTMLInputElement | HTMLTextAreaElement | null>;
      contentEditableRef?: never;
      onInput?: never;
    }
  | {
      inputRef?: never;
      /** works with ContentEditableDiv component and other content-editable elements */
      contentEditableRef: RefObject<
        (HTMLElement & ElementContentEditable) | null
      >;
      onInput?: never;
    }
  | {
      inputRef?: never;
      contentEditableRef?: never;
      /** Any complex input component with state management or other use case */
      onInput: (emoji: string) => void;
    };

export const EmojiPicker = ({
  inputRef,
  contentEditableRef,
  onInput,
}: EmojiPickerProps) => {
  const locale = useLocale() as Locale;
  const { mode, semanticColors } = useThemeValues();
  const [isLoaded, setIsLoaded] = useState<boolean>(false);

  useEffect(() => {
    import("emoji-picker-element").then(() => setIsLoaded(true));
  }, []);

  const localeProps = useMemo(() => {
    switch (locale) {
      case "de":
        return { locale: "de", i18n: de };
      case "es":
        return { locale: "es", i18n: es };
      case "fr":
        return { locale: "fr", i18n: fr };
      case "it":
        return { locale: "it", i18n: it };
      case "pl":
        return { locale: "pl", i18n: pl };
      case "pt":
        return { locale: "pt_BR", i18n: pt };
      case "en":
      case "en-PH":
      default:
        return { locale: "en", i18n: en };
    }
  }, [locale]);

  useEffect(() => {
    polyfillCountryFlagEmojis();
  }, []);

  const handleEmojiClick = (event: EmojiClickEvent) => {
    if (onInput) {
      onInput(event.detail.unicode);
      return;
    }

    if (contentEditableRef?.current) {
      const selection = window.getSelection();
      const range = selection?.rangeCount ? selection.getRangeAt(0) : null;

      // Create a new text node containing your custom text
      const textNode = document.createTextNode(event.detail.unicode);

      // Ensure the range we're about to mutate is actually inside this field
      if (
        !range ||
        !contentEditableRef.current.contains(range.commonAncestorContainer)
      ) {
        contentEditableRef.current.append(textNode);
      } else {
        // Delete selected text if the user has highlighted something
        range.deleteContents();

        // Insert the text node at the exact cursor position
        range.insertNode(textNode);

        // Optional: Move the cursor to the end of the newly inserted text
        range.setStartAfter(textNode);
        range.setEndAfter(textNode);
        selection.removeAllRanges();
        selection.addRange(range);
      }

      contentEditableRef.current.dispatchEvent(
        new Event("input", { bubbles: true }),
      );
      return;
    }

    if (inputRef?.current) {
      const { selectionStart, selectionEnd } = inputRef.current;
      if (selectionStart === null)
        inputRef.current.value = inputRef.current.value + event.detail.unicode;
      else {
        const pre = inputRef.current.value.slice(0, selectionStart);
        const post = inputRef.current.value.slice(selectionEnd);
        inputRef.current.value = pre + event.detail.unicode + post;
        inputRef.current.selectionStart = inputRef.current.selectionEnd =
          selectionStart + event.detail.unicode.length;
      }
      inputRef.current.dispatchEvent(new Event("input", { bubbles: true }));
      inputRef.current.dispatchEvent(new Event("change", { bubbles: true }));
    }
  };

  if (!isLoaded) return null;

  return createElement("emoji-picker", {
    ...localeProps,
    /**
     * Left to itself the element keys its palette off `prefers-color-scheme`,
     * which is the reader's OS setting rather than the theme they picked in the
     * app — so the picker came up light inside a dark app, and vice versa.
     * Its own `:host(.light)` / `:host(.dark)` rules are what this overrides
     * them with, and the class beats the media query.
     */
    className: mode === "dark" ? "dark" : "light",
    ref: (el) => {
      el.addEventListener("emoji-click", handleEmojiClick);
      /**
       * The two panel colors come from the theme rather than the element's own
       * defaults, whose dark grey sits visibly proud of the popover it is drawn
       * inside. Everything else the element themes itself.
       */
      el.setAttribute(
        "style",
        [
          `--emoji-font-family: ${fontFamilies.sansSerif}`,
          `--background: ${semanticColors.background}`,
          `--border-color: ${semanticColors.neutralsLowest}`,
          // The element sizes itself to `min-content`, so without this it
          // overflows a container too narrow for its eight columns instead of
          // tightening them.
          "max-width: 100%",
        ].join("; "),
      );
      return () => el.removeEventListener("emoji-click", handleEmojiClick);
    },
  });
};
